import { useEffect, useMemo, useState } from 'react';
import { Archive, CalendarClock, CheckCircle2, History, Info, Lightbulb, Loader2, Mail, Plus, RotateCcw, Save, Search, Send, Settings, Target, Users, Workflow, X } from 'lucide-react';
import { getAllClients, type ClientItem } from '../services/clientService';
import { getPartiesPrenantes, type PartiePrenante } from '../services/partiesPrenantesService';
import { useAuth } from '../contexts/AuthContext';
import {
  archiveTechPrimeFollowup,
  comiteTiStatuses,
  createTechPrimeFollowup,
  emptyTechPrimeFollowup,
  getTechPrimeFollowup,
  getTechPrimeFollowupAdminConfig,
  getTechPrimeFollowupEmailDraft,
  listTechPrimeFollowups,
  restoreTechPrimeFollowup,
  saveTechPrimeFollowupAdminConfig,
  sendTechPrimeFollowupEmail,
  techPrimeFollowupColors,
  techPrimeFollowupPriorities,
  techPrimeFollowupStatuses,
  updateTechPrimeFollowup,
  type TechPrimeFollowup,
  type TechPrimeFollowupEmailDraft,
} from '../services/techPrimeFollowupService';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

type TechPrimeFollowupsTab = 'registre' | 'fonctionnement';

function formatDate(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('fr-CA', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function dateOnly(value?: string | null) {
  if (!value) return '';
  return String(value).slice(0, 10);
}

function eventLabel(value: string) {
  if (value === 'created') return 'Création';
  if (value === 'updated') return 'Mise à jour';
  if (value === 'archived') return 'Archivage';
  if (value === 'restored') return 'Restauration';
  if (value === 'email_sent') return 'Courriel envoyé';
  return value;
}

function priorityClass(priority?: string) {
  if (priority === 'Critique') return 'bg-red-100 text-red-800 border-red-200';
  if (priority === 'Haute') return 'bg-amber-100 text-amber-800 border-amber-200';
  if (priority === 'Basse') return 'bg-slate-100 text-slate-700 border-slate-200';
  return 'bg-blue-100 text-blue-800 border-blue-200';
}

function sourceColorClass(color?: string) {
  if (color === 'Rouge') return 'border-l-red-600';
  if (color === 'Jaune') return 'border-l-yellow-400';
  return 'border-l-slate-300';
}

const TECH_PRIME_ADMIN_EMAILS = new Set(['jean-michel.caouette@bzinc.ca', 'jmcaouette@bzinc.ca']);

export default function TechPrimeFollowupsModal({ isOpen, onClose }: Props) {
  const { user } = useAuth();
  const [clients, setClients] = useState<ClientItem[]>([]);
  const [techniciens, setTechniciens] = useState<PartiePrenante[]>([]);
  const [followups, setFollowups] = useState<TechPrimeFollowup[]>([]);
  const [selected, setSelected] = useState<TechPrimeFollowup>(emptyTechPrimeFollowup());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterTechnicien, setFilterTechnicien] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterPriorite, setFilterPriorite] = useState('');
  const [viewMode, setViewMode] = useState<'active' | 'archives'>('active');
  const [activeTab, setActiveTab] = useState<TechPrimeFollowupsTab>('registre');
  const [emailDraft, setEmailDraft] = useState<TechPrimeFollowupEmailDraft | null>(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [availableTechnicienIds, setAvailableTechnicienIds] = useState<number[]>([]);
  const [hasAvailableTechnicienConfig, setHasAvailableTechnicienConfig] = useState(false);
  const [clientAssignments, setClientAssignments] = useState<Record<number, number[]>>({});
  const [adminSaving, setAdminSaving] = useState(false);

  const canAdmin = TECH_PRIME_ADMIN_EMAILS.has(String(user?.email || '').trim().toLowerCase());
  const isArchivedView = viewMode === 'archives' || Boolean(selected.archived);
  const availableTechniciens = useMemo(() => {
    if (!hasAvailableTechnicienConfig) return techniciens;
    const allowed = new Set(availableTechnicienIds.map(Number));
    return techniciens.filter((tech) => tech.id && allowed.has(Number(tech.id)));
  }, [availableTechnicienIds, hasAvailableTechnicienConfig, techniciens]);
  const selectedClientAssignedTechIds = selected.client_id ? clientAssignments[Number(selected.client_id)] || [] : [];
  const selectedClientTechniciens = useMemo(() => {
    if (!selectedClientAssignedTechIds.length) return availableTechniciens;
    const allowed = new Set(selectedClientAssignedTechIds.map(Number));
    return availableTechniciens.filter((tech) => tech.id && allowed.has(Number(tech.id)));
  }, [availableTechniciens, selectedClientAssignedTechIds]);
  const selectedClient = useMemo(() => clients.find((client) => String(client.id) === String(selected.client_id)), [clients, selected.client_id]);
  const selectedTech = useMemo(() => techniciens.find((tech) => String(tech.id) === String(selected.technicien_id)), [techniciens, selected.technicien_id]);

  useEffect(() => {
    if (!isOpen) return;
    let ignore = false;
    setLoading(true);
    setError('');
    Promise.all([
      getAllClients(),
      getPartiesPrenantes(2),
      listTechPrimeFollowups({ archived: viewMode === 'archives' }),
      getTechPrimeFollowupAdminConfig().catch(() => null),
    ])
      .then(([clientRows, techRows, followupRows, adminConfig]) => {
        if (ignore) return;
        setClients(clientRows.filter((client) => !client.archived));
        setTechniciens(techRows.filter((pp) => pp.actif !== false));
        setFollowups(followupRows);
        if (adminConfig) {
          setAvailableTechnicienIds(adminConfig.availableTechnicienIds);
          setHasAvailableTechnicienConfig(adminConfig.hasAvailableTechnicienConfig);
          setClientAssignments(
            adminConfig.assignments.reduce<Record<number, number[]>>((acc, assignment) => {
              const clientId = Number(assignment.client_id);
              const techIds = assignment.technicien_ids?.length ? assignment.technicien_ids : assignment.technicien_id ? [assignment.technicien_id] : [];
              acc[clientId] = Array.from(new Set([...(acc[clientId] || []), ...techIds.map(Number).filter(Boolean)]));
              return acc;
            }, {}),
          );
        }
      })
      .catch((err) => {
        if (!ignore) setError(err instanceof Error ? err.message : 'Erreur chargement suivis Tech Prime');
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const timeout = window.setTimeout(() => {
      void refreshList(viewMode);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [search, filterClient, filterTechnicien, filterStatut, filterPriorite, viewMode, isOpen]);

  async function refreshList(mode: 'active' | 'archives' = viewMode) {
    try {
      const rows = await listTechPrimeFollowups({
        search,
        clientId: filterClient,
        technicienId: filterTechnicien,
        statut: filterStatut,
        priorite: filterPriorite,
        archived: mode === 'archives',
      });
      setFollowups(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur chargement suivis');
    }
  }

  async function openFollowup(id?: number) {
    if (!id) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      setSelected(await getTechPrimeFollowup(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur ouverture suivi');
    } finally {
      setLoading(false);
    }
  }

  function startNew() {
    setViewMode('active');
    setSelected(emptyTechPrimeFollowup());
    setError('');
    setSuccess('');
  }

  function updateSelected<K extends keyof TechPrimeFollowup>(key: K, value: TechPrimeFollowup[K]) {
    setSelected((prev) => ({ ...prev, [key]: value }));
  }

  function updateSelectedClient(clientId: string) {
    const nextClientId = clientId ? Number(clientId) : '';
    const assignedTechIds = clientId ? clientAssignments[Number(clientId)] || [] : [];
    const currentTechId = selected.technicien_id ? Number(selected.technicien_id) : 0;
    const assignedTechId = assignedTechIds.includes(currentTechId) ? currentTechId : assignedTechIds[0];
    setSelected((prev) => ({
      ...prev,
      client_id: nextClientId,
      technicien_id: assignedTechId || '',
    }));
  }

  function toggleAvailableTechnicien(technicienId?: number) {
    if (!technicienId) return;
    setHasAvailableTechnicienConfig(true);
    setAvailableTechnicienIds((prev) => {
      const base = hasAvailableTechnicienConfig ? prev : techniciens.map((tech) => Number(tech.id)).filter(Boolean);
      const exists = base.map(Number).includes(Number(technicienId));
      return exists ? base.filter((id) => Number(id) !== Number(technicienId)) : [...base, Number(technicienId)];
    });
    setClientAssignments((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((clientId) => {
        next[Number(clientId)] = (next[Number(clientId)] || []).filter((id) => Number(id) !== Number(technicienId));
      });
      return next;
    });
  }

  function toggleClientAssignment(clientId?: number, technicienId?: number) {
    if (!clientId || !technicienId) return;
    setClientAssignments((prev) => ({
      ...prev,
      [Number(clientId)]: (prev[Number(clientId)] || []).map(Number).includes(Number(technicienId))
        ? (prev[Number(clientId)] || []).filter((id) => Number(id) !== Number(technicienId))
        : [...(prev[Number(clientId)] || []), Number(technicienId)],
    }));
  }

  async function saveAdminConfig() {
    setAdminSaving(true);
    setError('');
    setSuccess('');
    try {
      const saved = await saveTechPrimeFollowupAdminConfig({
        availableTechnicienIds: hasAvailableTechnicienConfig ? availableTechnicienIds.map(Number).filter(Boolean) : techniciens.map((tech) => Number(tech.id)).filter(Boolean),
        assignments: Object.entries(clientAssignments)
          .map(([clientId, technicienIds]) => ({
            client_id: Number(clientId),
            technicien_id: null,
            technicien_ids: technicienIds
              .map(Number)
              .filter((id) => availableTechniciens.some((tech) => Number(tech.id) === Number(id))),
          }))
          .filter((assignment) => assignment.client_id && assignment.technicien_ids.length),
      });
      setAvailableTechnicienIds(saved.availableTechnicienIds);
      setHasAvailableTechnicienConfig(saved.hasAvailableTechnicienConfig);
      setClientAssignments(
        saved.assignments.reduce<Record<number, number[]>>((acc, assignment) => {
          const clientId = Number(assignment.client_id);
          const techIds = assignment.technicien_ids?.length ? assignment.technicien_ids : assignment.technicien_id ? [assignment.technicien_id] : [];
          acc[clientId] = Array.from(new Set([...(acc[clientId] || []), ...techIds.map(Number).filter(Boolean)]));
          return acc;
        }, {}),
      );
      setSuccess('Configuration Administration sauvegardée.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur sauvegarde Administration');
    } finally {
      setAdminSaving(false);
    }
  }

  async function handleSave() {
    if (selected.archived) {
      setError('Restaure le suivi avant de le modifier.');
      return;
    }
    if (!selected.client_id) {
      setError('Choisis un client PRIsME avant de sauvegarder.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload: TechPrimeFollowup = {
        ...selected,
        client_id: Number(selected.client_id),
        technicien_id: selected.technicien_id ? Number(selected.technicien_id) : undefined,
      };
      const saved = selected.id ? await updateTechPrimeFollowup(selected.id, payload) : await createTechPrimeFollowup(payload);
      setSelected(saved);
      setSuccess('Suivi sauvegardé.');
      await refreshList('active');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur sauvegarde suivi');
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive() {
    if (!selected.id) return;
    const confirmed = window.confirm('Archiver ce suivi client?');
    if (!confirmed) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await archiveTechPrimeFollowup(selected.id);
      setSelected(emptyTechPrimeFollowup());
      setViewMode('archives');
      await refreshList('archives');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur archivage');
    } finally {
      setSaving(false);
    }
  }

  async function handleRestore() {
    if (!selected.id) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const restored = await restoreTechPrimeFollowup(selected.id);
      setViewMode('active');
      setSelected(restored);
      await refreshList('active');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur restauration');
    } finally {
      setSaving(false);
    }
  }

  async function prepareEmail() {
    if (!selected.id) {
      setError('Sauvegarde le suivi avant de préparer un courriel.');
      return;
    }
    setEmailBusy(true);
    setError('');
    try {
      setEmailDraft(await getTechPrimeFollowupEmailDraft(selected.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur génération courriel');
    } finally {
      setEmailBusy(false);
    }
  }

  async function sendEmail() {
    if (!selected.id || !emailDraft) return;
    setEmailBusy(true);
    setError('');
    setSuccess('');
    try {
      const result = await sendTechPrimeFollowupEmail(selected.id, emailDraft);
      setSelected(result.followup);
      setEmailDraft(null);
      setSuccess(result.message || 'Courriel envoyé.');
      await refreshList(viewMode);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur envoi courriel');
    } finally {
      setEmailBusy(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm p-2 lg:p-3">
      <div className="mx-auto flex h-full max-w-[98vw] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/20">
        <header className="relative flex items-center justify-between overflow-hidden border-b border-slate-900 px-5 py-4">
          <div className="absolute inset-0 bg-slate-950" />
          <div className="absolute inset-y-0 left-0 w-2/3 bg-linear-to-r from-indigo-800 via-blue-700 to-cyan-600 opacity-90" />
          <div className="absolute inset-y-0 right-0 w-1/2 bg-linear-to-l from-slate-950 via-slate-950/95 to-transparent" />
          <div className="relative">
            <div className="mb-1 inline-flex items-center rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-cyan-50">
              Techs Primes · Suivis clients
            </div>
            <h2 className="text-2xl font-extrabold text-white">Gestion des suivis clients</h2>
            <p className="text-xs font-medium text-cyan-50">Registre simple basé sur la liste Prime: projets, comité TI, prochain suivi, actions et courriels.</p>
          </div>
          <div className="relative flex items-center gap-2">
            {canAdmin && (
              <button
                type="button"
                onClick={() => setIsAdminOpen(true)}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-white/40 bg-white/95 px-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-white"
              >
                <Settings className="h-4 w-4" />
                Administration
              </button>
            )}
            <button
              type="button"
              onClick={prepareEmail}
              disabled={emailBusy || !selected.id}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-white/40 bg-white/95 px-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-white disabled:opacity-50"
            >
              {emailBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Courriel
            </button>
            <button
              type="button"
              onClick={startNew}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-white/40 bg-white/95 px-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-white"
            >
              <Plus className="h-4 w-4" />
              Nouveau
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || isArchivedView}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-blue-500 px-3 text-sm font-semibold text-white shadow-sm shadow-blue-950/30 hover:bg-blue-400 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Sauvegarder
            </button>
            <button type="button" onClick={onClose} className="rounded-lg p-2 text-white/75 hover:bg-white/10 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>
        </header>

        {error && <div className="border-b border-red-100 bg-red-50 px-5 py-2 text-sm text-red-700">{error}</div>}
        {success && <div className="border-b border-emerald-100 bg-emerald-50 px-5 py-2 text-sm text-emerald-700">{success}</div>}
        {isArchivedView && selected.id && (
          <div className="border-b border-amber-100 bg-amber-50 px-5 py-2 text-sm font-medium text-amber-800">
            Ce suivi est archivé. Il peut être consulté, mais doit être restauré avant modification ou envoi.
          </div>
        )}

        <div className="flex flex-wrap gap-2 border-b border-slate-300 bg-white px-5 py-3">
          <button
            type="button"
            onClick={() => setActiveTab('registre')}
            className={`inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-bold ${
              activeTab === 'registre' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
            }`}
          >
            <CheckCircle2 className="h-4 w-4" />
            Registre
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('fonctionnement')}
            className={`inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-bold ${
              activeTab === 'fonctionnement' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
            }`}
          >
            <Info className="h-4 w-4" />
            Fonctionnement
          </button>
        </div>

        {activeTab === 'registre' ? (
        <div className="grid min-h-0 flex-1 grid-cols-1 bg-slate-100 lg:grid-cols-[410px_1fr]">
          <aside className="flex min-h-0 flex-col border-r border-slate-300 bg-slate-50">
            <div className="space-y-2 border-b border-slate-300 bg-white p-3">
              <div className="grid grid-cols-2 rounded-xl border border-slate-300 bg-slate-100 p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => {
                    setViewMode('active');
                    setSelected(emptyTechPrimeFollowup());
                  }}
                  className={`h-8 rounded-lg text-xs font-semibold transition ${viewMode === 'active' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-cyan-50'}`}
                >
                  Actifs
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewMode('archives');
                    setSelected(emptyTechPrimeFollowup());
                  }}
                  className={`h-8 rounded-lg text-xs font-semibold transition ${viewMode === 'archives' ? 'bg-amber-600 text-white shadow-sm' : 'text-slate-600 hover:bg-amber-50'}`}
                >
                  Archives
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Client, projet, tech, résumé..."
                  className="h-10 w-full rounded-xl border border-slate-300 bg-white pl-8 pr-3 text-sm text-slate-900 outline-none shadow-sm placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)} className="h-10 rounded-xl border border-slate-300 bg-white px-2 text-xs text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                  <option value="">Tous clients</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.nom}
                    </option>
                  ))}
                </select>
                <select value={filterTechnicien} onChange={(e) => setFilterTechnicien(e.target.value)} className="h-10 rounded-xl border border-slate-300 bg-white px-2 text-xs text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                  <option value="">Tous techs</option>
                  {availableTechniciens.map((tech) => (
                    <option key={tech.id} value={tech.id}>
                      {[tech.prenom, tech.nom].filter(Boolean).join(' ') || tech.email}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select value={filterStatut} onChange={(e) => setFilterStatut(e.target.value)} className="h-10 rounded-xl border border-slate-300 bg-white px-2 text-xs text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                  <option value="">Tous statuts</option>
                  {techPrimeFollowupStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                <select value={filterPriorite} onChange={(e) => setFilterPriorite(e.target.value)} className="h-10 rounded-xl border border-slate-300 bg-white px-2 text-xs text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                  <option value="">Toutes priorités</option>
                  {techPrimeFollowupPriorities.map((priority) => (
                    <option key={priority} value={priority}>
                      {priority}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-3">
              {loading && (
                <div className="flex items-center gap-2 rounded-lg bg-white p-3 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Chargement...
                </div>
              )}
              {!loading && followups.length === 0 && (
                <div className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">
                  {viewMode === 'archives' ? 'Aucun suivi archivé.' : 'Aucun suivi actif trouvé.'}
                </div>
              )}
              <div className="space-y-2">
                {followups.map((row) => (
                  <button
                    type="button"
                    key={row.id}
                    onClick={() => void openFollowup(row.id)}
                    className={`w-full rounded-2xl border border-l-4 p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${sourceColorClass(row.couleur_source)} ${
                      selected.id === row.id ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-slate-300 bg-white hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-semibold text-slate-900">{row.client_nom || row.titre}</div>
                        <div className="mt-0.5 text-xs text-slate-500">{row.technicien_nom || 'Tech Prime à choisir'}</div>
                      </div>
                      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${priorityClass(row.priorite)}`}>
                        {row.priorite}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
                      <CalendarClock className="h-3.5 w-3.5" />
                      <span>{row.prochain_suivi ? `Prochain: ${dateOnly(row.prochain_suivi)}` : 'Prochain suivi non défini'}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                      <span>{row.statut}</span>
                      <span>{formatDate(row.updated_at)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <main className="min-h-0 overflow-y-auto p-5">
            <div className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
              <div className="grid grid-cols-1 gap-3 xl:grid-cols-5">
                <label className="text-xs font-semibold text-slate-600">
                  Client PRIsME
                  <select value={selected.client_id} onChange={(e) => updateSelectedClient(e.target.value)} className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                    <option value="">Choisir...</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.nom}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  Tech Prime
                  <select value={selected.technicien_id || ''} onChange={(e) => updateSelected('technicien_id', e.target.value ? Number(e.target.value) : '')} className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                    <option value="">Choisir...</option>
                    {selectedClientTechniciens.map((tech) => (
                      <option key={tech.id} value={tech.id}>
                        {[tech.prenom, tech.nom].filter(Boolean).join(' ') || tech.email}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  Statut
                  <select value={selected.statut} onChange={(e) => updateSelected('statut', e.target.value as TechPrimeFollowup['statut'])} className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                    {techPrimeFollowupStatuses.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  Priorité
                  <select value={selected.priorite} onChange={(e) => updateSelected('priorite', e.target.value as TechPrimeFollowup['priorite'])} className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                    {techPrimeFollowupPriorities.map((priority) => (
                      <option key={priority} value={priority}>
                        {priority}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  Couleur source
                  <select value={selected.couleur_source} onChange={(e) => updateSelected('couleur_source', e.target.value as TechPrimeFollowup['couleur_source'])} className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                    {techPrimeFollowupColors.map((color) => (
                      <option key={color} value={color}>
                        {color}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-5">
                <label className="text-xs font-semibold text-slate-600">
                  Comité TI
                  <select value={selected.comite_ti_statut || ''} onChange={(e) => updateSelected('comite_ti_statut', e.target.value)} className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                    <option value="">Choisir...</option>
                    {comiteTiStatuses.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  Dernière visite / meeting
                  <input value={selected.derniere_visite || ''} onChange={(e) => updateSelected('derniere_visite', e.target.value)} className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  Prochain suivi
                  <input type="date" value={dateOnly(selected.prochain_suivi)} onChange={(e) => updateSelected('prochain_suivi', e.target.value || null)} className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  Canal
                  <input value={selected.canal || ''} onChange={(e) => updateSelected('canal', e.target.value)} placeholder="Courriel, Teams, appel..." className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  Destinataires courriel
                  <input value={selected.destinataires || selectedTech?.email || ''} onChange={(e) => updateSelected('destinataires', e.target.value)} placeholder="email1@..., email2@..." className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                </label>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
              {[
                ['Client', selectedClient?.nom || selected.client_nom || 'À choisir', 'from-blue-50 to-cyan-50', 'text-blue-800'],
                ['Tech Prime', [selectedTech?.prenom, selectedTech?.nom].filter(Boolean).join(' ') || selected.technicien_nom || 'À choisir', 'from-indigo-50 to-blue-50', 'text-indigo-800'],
                ['Courriel', selected.dernier_courriel_at ? formatDate(selected.dernier_courriel_at) : 'Pas envoyé', 'from-emerald-50 to-teal-50', 'text-emerald-800'],
                ['Historique', String(selected.history?.length || 0), 'from-violet-50 to-fuchsia-50', 'text-violet-800'],
              ].map(([label, value, gradient, textColor]) => (
                <div key={label} className={`rounded-2xl border border-slate-200 bg-linear-to-br ${gradient} p-3 shadow-md shadow-slate-200/70`}>
                  <div className="text-xs font-semibold text-slate-500">{label}</div>
                  <div className={`truncate text-lg font-extrabold ${textColor}`}>{value}</div>
                </div>
              ))}
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-2">
              <section className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center gap-2 text-sm font-extrabold text-slate-900">
                  <Users className="h-4 w-4 text-blue-600" />
                  Infos du fichier Prime
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <label className="text-xs font-semibold text-slate-600">
                    Plan directeur
                    <textarea value={selected.plan_directeur || ''} onChange={(e) => updateSelected('plan_directeur', e.target.value)} className="mt-1 h-24 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-inner outline-none placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                  </label>
                  <label className="text-xs font-semibold text-slate-600">
                    Projets à venir
                    <textarea value={selected.projets_a_venir || ''} onChange={(e) => updateSelected('projets_a_venir', e.target.value)} className="mt-1 h-36 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-inner outline-none placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                  </label>
                  <label className="text-xs font-semibold text-slate-600">
                    Note interne
                    <textarea value={selected.note_interne || ''} onChange={(e) => updateSelected('note_interne', e.target.value)} className="mt-1 h-28 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-inner outline-none placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                  </label>
                </div>
              </section>

              <section className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center gap-2 text-sm font-extrabold text-slate-900">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  Compte rendu de suivi
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <label className="text-xs font-semibold text-slate-600">
                    Résumé du suivi
                    <textarea value={selected.resume_suivi || ''} onChange={(e) => updateSelected('resume_suivi', e.target.value)} className="mt-1 h-32 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-inner outline-none placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                  </label>
                  <label className="text-xs font-semibold text-slate-600">
                    Actions BZ
                    <textarea value={selected.actions_bz || ''} onChange={(e) => updateSelected('actions_bz', e.target.value)} className="mt-1 h-32 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-inner outline-none placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                  </label>
                  <label className="text-xs font-semibold text-slate-600">
                    Attentes client / décision requise
                    <div className="mt-1 grid grid-cols-1 gap-2 lg:grid-cols-2">
                      <textarea value={selected.attentes_client || ''} onChange={(e) => updateSelected('attentes_client', e.target.value)} placeholder="Attentes client" className="h-28 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-inner outline-none placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                      <textarea value={selected.decision_requise || ''} onChange={(e) => updateSelected('decision_requise', e.target.value)} placeholder="Décision requise" className="h-28 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-inner outline-none placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                    </div>
                  </label>
                </div>
              </section>
            </div>

            {selected.id && (
              <div className="mt-5 rounded-2xl border border-violet-100 bg-linear-to-br from-violet-50 via-white to-cyan-50 p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <div className="font-bold text-slate-900">Historique</div>
                  {isArchivedView ? (
                    <button type="button" onClick={handleRestore} disabled={saving} className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-white px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50">
                      <RotateCcw className="h-3.5 w-3.5" />
                      Restaurer
                    </button>
                  ) : (
                    <button type="button" onClick={handleArchive} disabled={saving} className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-white px-2 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-50">
                      <Archive className="h-3.5 w-3.5" />
                      Archiver
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  {(selected.history || []).slice(0, 12).map((event) => (
                    <div key={event.id} className="rounded-lg bg-white px-3 py-2 text-xs text-slate-600">
                      <span className="font-semibold text-slate-900">{eventLabel(event.event_type)}</span>
                      {' · '}
                      {formatDate(event.created_at)}
                      {event.actor_email ? ` · ${event.actor_email}` : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </main>
        </div>
        ) : (
          <main className="min-h-0 flex-1 overflow-y-auto bg-slate-100 p-5">
            <section className="space-y-5">
              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-bold uppercase text-blue-800">
                      <Lightbulb className="h-3.5 w-3.5" />
                      Esprit de la tuile
                    </div>
                    <h3 className="mt-3 text-2xl font-black text-slate-950">
                      Garder le prochain suivi client visible et actionnable.
                    </h3>
                    <p className="mt-2 max-w-4xl text-sm leading-relaxed text-slate-600">
                      Cette tuile sert à convertir la liste Prime en suivis concrets: qui suit le client, ce qui est prévu,
                      ce qui bloque, quand reprendre contact et quel message envoyer.
                    </p>
                  </div>
                  <div className="rounded-lg bg-slate-950 px-4 py-3 text-sm font-bold text-white">
                    Prime · Suivi · Actions BZ · Courriel
                  </div>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
                    <Target className="h-5 w-5" />
                  </div>
                  <h4 className="mt-4 font-black text-slate-950">1. Partir du bon contexte</h4>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Sélectionner le client, le Tech Prime, la priorité, la couleur source et les informations issues du
                    fichier Prime pour voir rapidement pourquoi ce client doit être suivi.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-100 text-cyan-700">
                    <Workflow className="h-5 w-5" />
                  </div>
                  <h4 className="mt-4 font-black text-slate-950">2. Planifier le suivi</h4>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Documenter le comité TI, la dernière visite, le prochain suivi, le canal, le résumé, les attentes du
                    client, les décisions requises et les actions BZ à reprendre.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                    <Mail className="h-5 w-5" />
                  </div>
                  <h4 className="mt-4 font-black text-slate-950">3. Fermer la boucle</h4>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Préparer le courriel, archiver ce qui est complété et garder l’historique pour savoir quand le suivi a
                    été mis à jour, restauré ou communiqué au client.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2 font-black text-slate-950">
                  <History className="h-4 w-4" />
                  Règle simple
                </div>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Chaque client Prime devrait avoir un prochain geste clair: une date, un responsable, une action BZ ou un
                  courriel prêt à envoyer. Si le prochain geste n’est pas clair, la fiche n’est pas terminée.
                </p>
              </div>
            </section>
          </main>
        )}
      </div>

      {isAdminOpen && canAdmin && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/70 p-4">
          <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-950 px-5 py-4 text-white">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-cyan-100">Administration Tech Prime</div>
                <div className="text-lg font-extrabold">Associations clients et disponibilité</div>
              </div>
              <button type="button" onClick={() => setIsAdminOpen(false)} className="rounded-lg p-2 text-white/75 hover:bg-white/10 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 p-5">
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-[360px_1fr]">
                <section className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
                  <div className="mb-3">
                    <h3 className="text-sm font-extrabold text-slate-900">Tech Primes disponibles</h3>
                    <p className="mt-1 text-xs text-slate-500">Coche les personnes qui doivent apparaître dans le menu déroulant Tech Prime.</p>
                  </div>
                  <div className="space-y-2">
                    {techniciens.map((tech) => {
                      const techId = Number(tech.id);
                      const checked = !hasAvailableTechnicienConfig || availableTechnicienIds.map(Number).includes(techId);
                      return (
                        <label key={tech.id} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm shadow-sm transition ${checked ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleAvailableTechnicien(techId)}
                            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="min-w-0">
                            <span className="block font-bold text-slate-900">{[tech.prenom, tech.nom].filter(Boolean).join(' ') || tech.email || `Tech ${tech.id}`}</span>
                            {tech.email && <span className="block truncate text-xs text-slate-500">{tech.email}</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
                  <div className="mb-3">
                    <h3 className="text-sm font-extrabold text-slate-900">Clients liés aux Tech Primes</h3>
                    <p className="mt-1 text-xs text-slate-500">Coche un ou plusieurs Tech Primes par client. Quand le client est sélectionné dans un suivi, seuls ces Tech Primes seront proposés.</p>
                  </div>
                  <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
                    {clients.map((client) => {
                      const clientId = Number(client.id);
                      const assignedIds = clientAssignments[clientId] || [];
                      const visibleAssignedIds = assignedIds.filter((id) => availableTechniciens.some((tech) => Number(tech.id) === Number(id)));
                      return (
                        <div key={client.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs font-semibold text-slate-600">
                          <div className="flex items-center justify-between gap-2">
                            <span className="block truncate text-sm font-extrabold text-slate-900">{client.nom}</span>
                            <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-slate-500">
                              {visibleAssignedIds.length || 'Non lié'}
                            </span>
                          </div>
                          <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                            {availableTechniciens.map((tech) => {
                              const techId = Number(tech.id);
                              const checked = assignedIds.map(Number).includes(techId);
                              return (
                                <label key={tech.id} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-2 py-1.5 ${checked ? 'border-blue-200 bg-blue-50 text-blue-900' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100'}`}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleClientAssignment(clientId, techId)}
                                    className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="truncate">{[tech.prenom, tech.nom].filter(Boolean).join(' ') || tech.email}</span>
                                </label>
                              );
                            })}
                            {availableTechniciens.length === 0 && (
                              <div className="rounded-lg border border-dashed border-slate-300 bg-white px-2 py-2 text-xs text-slate-500">
                                Aucun Tech Prime disponible.
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-white px-5 py-3">
              <button type="button" onClick={() => setIsAdminOpen(false)} disabled={adminSaving} className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                Fermer
              </button>
              <button type="button" onClick={saveAdminConfig} disabled={adminSaving} className="inline-flex h-10 items-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
                {adminSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Sauvegarder l'administration
              </button>
            </div>
          </div>
        </div>
      )}

      {emailDraft && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/70 p-4">
          <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-950 px-5 py-4 text-white">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-cyan-100">Courriel de suivi</div>
                <div className="text-lg font-extrabold">{selected.client_nom || selectedClient?.nom || 'Client PRIsME'}</div>
              </div>
              <button type="button" onClick={() => setEmailDraft(null)} className="rounded-lg p-2 text-white/75 hover:bg-white/10 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-5">
              <div className="grid grid-cols-1 gap-3">
                <label className="text-xs font-semibold text-slate-600">
                  Destinataires
                  <input value={emailDraft.to} onChange={(e) => setEmailDraft({ ...emailDraft, to: e.target.value })} className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  Sujet
                  <input value={emailDraft.subject} onChange={(e) => setEmailDraft({ ...emailDraft, subject: e.target.value })} className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  HTML du courriel
                  <textarea value={emailDraft.html} onChange={(e) => setEmailDraft({ ...emailDraft, html: e.target.value })} className="mt-1 h-72 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2 font-mono text-xs text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
                </label>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
              <button type="button" onClick={() => setEmailDraft(null)} disabled={emailBusy} className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                Annuler
              </button>
              <button type="button" onClick={sendEmail} disabled={emailBusy || !emailDraft.to || !emailDraft.subject || !emailDraft.html} className="inline-flex h-10 items-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
                {emailBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
